import type { ClientReview, ClientVersion, TaskType } from '@mybusinessmate/schema';
import { formatDay, shortMonth } from './project-display';

const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const PLATFORM: Record<string, string> = { instagram: 'Instagram', facebook: 'Facebook' };

/** "just now", "5m ago", "2h ago", then a date: the client pages' short way of saying when. */
export function sentAgo(iso: string, now: Date = new Date()): string {
  const minutes = Math.floor(Math.max(0, now.getTime() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)}h ago`;
  return formatDay(iso);
}

/** "Sat 4 Oct, 7:00 PM", from when the post is planned to go out. */
export const goesLive = (iso: string) => {
  const at = new Date(iso);
  return `${shortMonth(weekday.format(at)).replace(',', '')}, ${time.format(at)}`;
};

export const platformsText = (platforms: string[]) => platforms.map((p) => PLATFORM[p] ?? p).join(', ');

/** The words of a post or reel as it will read: caption, then hashtags. */
export function captionOf(version: ClientVersion): string {
  const d = version.details;
  if (d.type !== 'post' && d.type !== 'reel') return '';
  return [d.caption ?? '', d.hashtags.map((t) => `#${t}`).join(' ')].filter(Boolean).join(' ');
}

/** The rows of the "Details" card: what the client wants to know before saying yes. */
export function detailRows(review: ClientReview, version: ClientVersion): { label: string; value: string }[] {
  const d = version.details;
  const rows: { label: string; value: string | null }[] = [];
  if (d.type === 'post' || d.type === 'reel') {
    rows.push({ label: 'Goes live', value: d.scheduledUploadAt ? goesLive(d.scheduledUploadAt) : null });
    rows.push({ label: 'Platforms', value: d.platforms.length ? platformsText(d.platforms) : null });
    rows.push({
      label: 'Format',
      value:
        d.type === 'reel'
          ? 'Reel · 9:16'
          : version.assets.length > 1
            ? `Carousel · ${version.assets.length} images`
            : 'Single image',
    });
  } else if (d.type === 'website') {
    rows.push({ label: 'Preview', value: d.liveUrl });
  } else if (d.type === 'monthly_update') {
    rows.push({ label: 'Page', value: d.pageUrl });
  }
  rows.push({ label: 'Project', value: review.task.projectName });
  return rows.flatMap((r) => (r.value ? [{ label: r.label, value: r.value }] : []));
}

/** The chips under "What needs changing?": the parts of this kind of work a client can point at. */
export function feedbackAreas(type: TaskType, slides: number): string[] {
  switch (type) {
    case 'post':
      return [
        'Caption',
        ...Array.from({ length: Math.min(slides, 6) }, (_, i) => `Image ${i + 1}`),
        'Colours',
        'Price',
      ];
    case 'reel':
      return ['Caption', 'Video', 'Audio', 'Text on screen', 'Price'];
    case 'website':
      return ['Wording', 'Images', 'Layout', 'Colours', 'Prices'];
    default:
      return ['Wording', 'Files', 'Something else'];
  }
}

/** "Rahul, designer" (or just "Rahul"): who made the version, as the client is told. */
export const madeBy = (version: ClientVersion) =>
  version.sentBy ? [version.sentBy.name, version.sentBy.title].filter(Boolean).join(', ') : 'your team';

// --- The client's task page (Figma C03.3) -------------------------------------------------------------------------

const shortDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const shortTime = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

/** "25 Sep, 6:10 PM". */
export const dayAndTime = (iso: string) =>
  `${shortMonth(shortDate.format(new Date(iso)))}, ${shortTime.format(new Date(iso))}`;

/** "42 MB", "1080 KB": a file's size the way the deliverables list says it. */
export function fileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / 1024 / 1024)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** "MP4 · 42 MB": a deliverable's second line. */
export const fileMeta = (asset: { name: string; size: number }) =>
  `${(asset.name.split('.').pop() ?? '').toUpperCase()} · ${fileSize(asset.size)}`;

/** "today", "yesterday", or "21 Oct": how the task page says when something was sent. */
export function dayWord(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(at)) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return formatDay(iso);
}

/** "Aisha, designer" is how a person is named to the client: first name, then what they do. */
export const workerText = (worker: { name: string; title: string | null }) =>
  [worker.name, worker.title].filter(Boolean).join(', ');

/** "Carousel · 4 slides · 4:5", "Single image · 4:5", "Reel · 9:16": the shape of the work, before or after it is made. */
export function formatText(review: ClientReview, version: ClientVersion | null): string | null {
  const d = version?.details ?? review.brief?.details;
  if (!d || (d.type !== 'post' && d.type !== 'reel')) return null;
  if (d.type === 'reel') return 'Reel · 9:16';
  const slides = version?.assets.length ?? 0;
  const carousel = slides > 1 || ('format' in d && d.format === 'carousel');
  return carousel
    ? ['Carousel', slides > 1 ? `${slides} slides` : null, '4:5'].filter(Boolean).join(' · ')
    : 'Single image · 4:5';
}

export interface TimelineEntry {
  key: string;
  /** The dot: blue for the one in play, grey for history. */
  current: boolean;
  title: string;
  sub: string;
  tag: { label: string; tone: 'blue' | 'amber' | 'green' } | null;
}

/** The first sentence or line of the author's note, for a version's title ("Shorter intro, bigger price tag"). */
const headline = (note: string | null, fallback: string) => {
  const first = (note ?? '').split(/[\n.]/)[0]?.trim();
  return first ? (first.length > 60 ? `${first.slice(0, 57)}…` : first) : fallback;
};

/**
 * "Versions & activity" (Figma C03.3), newest first: each version the team sent, what the client said about it
 * where they asked for changes, and the brief at the bottom.
 */
export function clientTimeline(review: ClientReview): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  review.versions.forEach((version, index) => {
    const current = index === 0;
    const approved = version.clientStatus === 'approved';
    // Newest first: what the client said about a version comes above the version itself.
    if (version.clientStatus === 'revision_requested' && version.feedback) {
      out.push({
        key: `${version.id}-asked`,
        current: false,
        title: `You asked for changes on v${version.versionNumber}`,
        sub: `“${version.feedback.replace(/\s\([^()]*\)$/, '')}”${version.reviewedAt ? ` · ${formatDay(version.reviewedAt)}` : ''}`,
        tag: { label: 'Changes asked', tone: 'amber' },
      });
    }
    if (approved) {
      // Saying yes is the line for that version (Figma C03.10b): when they approved it, and that it is done.
      out.push({
        key: version.id,
        current,
        title: `Approved by you · v${version.versionNumber}`,
        sub: version.reviewedAt ? dayAndTime(version.reviewedAt) : dayAndTime(version.sentAt),
        tag: { label: 'Done', tone: 'blue' },
      });
      return;
    }
    out.push({
      key: version.id,
      current,
      title: `v${version.versionNumber} · ${headline(version.whatChanged, 'Sent for your review')}`,
      // Only the version in play says who made it; the older ones are history, so the day is enough.
      sub: current
        ? `${version.sentBy?.name ?? 'Your team'} · ${dayWord(version.sentAt)}, ${shortTime.format(new Date(version.sentAt))}`
        : dayAndTime(version.sentAt),
      tag: current ? { label: 'Current', tone: 'blue' } : null,
    });
  });
  if (review.brief) {
    out.push({
      key: 'brief',
      current: false,
      title: 'v0 · Your brief',
      sub: `${review.brief.byClient ? 'Submitted by you' : 'Written by your team'} · ${formatDay(review.brief.createdAt)}`,
      tag: null,
    });
  }
  return out;
}
